import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
    Download, 
    Loader2, 
    RotateCcw 
} from 'lucide-react';
import { useReportFilters } from '@/hooks/useReportFilters';
import { TimePeriodSelection } from './TimePeriodSelection';
import { DesignationFilter } from './DesignationFilter';
import { useToast } from '@/hooks/use-toast';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import axios from 'axios';
import { baseUrl } from '@/constants/api';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

interface ReportModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const ReportModal: React.FC<ReportModalProps> = ({ open, onOpenChange }) => {
    const { toast } = useToast();
    const { token } = useSelector((state: RootState) => state.account);
    const {
        filters,
        updateFilter,
        resetFilters,
        designations,
        isLoadingDesignations,
        refetchDesignations,
    } = useReportFilters();

    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);

    const handleDownload = async () => {
        if (!token) {
            toast({
                title: "Authentication Required",
                description: "You must be logged in to generate reports.",
                variant: "destructive",
            });
            return;
        }

        setIsGenerating(true);
        setProgress(10);
        
        try {
            // Simulate progress for UI experience
            const progressInterval = setInterval(() => {
                setProgress(prev => (prev < 90 ? prev + 10 : prev));
            }, 500);

            // API Call for report generation - Hardcoded to PDF
            const response = await axios.post(`${baseUrl}/api/quiz-reports/generate`, {
                filters: {
                    ...filters,
                    format: 'PDF',
                    startDate: filters.timePeriod === 'Custom' ? filters.startDate : undefined,
                    endDate: filters.timePeriod === 'Custom' ? filters.endDate : undefined,
                }
            }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            clearInterval(progressInterval);
            setProgress(100);

            // Trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `Quiz_Report_${new Date().toISOString().split('T')[0]}.pdf`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast({
                title: "Report Generated",
                description: `Your PDF report has been successfully downloaded.`,
                className: "bg-green-50 border-green-200",
            });

            setTimeout(() => {
                onOpenChange(false);
                setIsGenerating(false);
                setProgress(0);
            }, 500);

        } catch (error: any) {
            console.error('Report generation failed:', error);
            
            let errorMessage = "An error occurred while generating your report. Please try again.";
            if (error.response?.status === 429) {
                errorMessage = "Too many requests. Please wait a minute before trying again.";
            } else if (!navigator.onLine) {
                errorMessage = "Unable to connect to server. Please check your connection.";
            }

            toast({
                title: "Generation Failed",
                description: errorMessage,
                variant: "destructive",
            });
            
            setIsGenerating(false);
            setProgress(0);
        }
    };

    const handleDateChange = (start: Date | null, end: Date | null) => {
        updateFilter('startDate', start ? start.toISOString() : null);
        updateFilter('endDate', end ? end.toISOString() : null);
    };

    return (
        <Dialog open={open} onOpenChange={(val) => !isGenerating && onOpenChange(val)}>
            <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden" aria-describedby="report-modal-description">
                <DialogHeader className="p-6 pb-2">
                    <DialogTitle className="text-xl font-bold flex items-center gap-2">
                        <Download className="h-5 w-5 text-primary" />
                        Download Report
                    </DialogTitle>
                    <DialogDescription id="report-modal-description">
                        Select time period and designations for the PDF report.
                    </DialogDescription>
                </DialogHeader>

                <ScrollArea className="flex-1 px-6 py-2">
                    <div className="space-y-6 pb-6">
                        <Separator />
                        
                        <TimePeriodSelection
                            value={filters.timePeriod}
                            startDate={filters.startDate ? new Date(filters.startDate) : null}
                            endDate={filters.endDate ? new Date(filters.endDate) : null}
                            onChange={(val) => updateFilter('timePeriod', val)}
                            onDateChange={handleDateChange}
                        />

                        <Separator />

                        <DesignationFilter
                            designations={designations}
                            selected={filters.designations}
                            onChange={(val) => updateFilter('designations', val)}
                            isLoading={isLoadingDesignations}
                        />
                    </div>
                </ScrollArea>

                <DialogFooter className="p-6 pt-2 bg-muted/20 border-t flex-col sm:flex-row gap-3">
                    <Button
                        variant="ghost"
                        onClick={resetFilters}
                        disabled={isGenerating}
                        className="flex-1 sm:flex-none text-muted-foreground"
                    >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset Filters
                    </Button>
                    
                    <Button
                        onClick={handleDownload}
                        disabled={isGenerating || (filters.timePeriod === 'Custom' && (!filters.startDate || !filters.endDate))}
                        className="flex-1 relative overflow-hidden"
                    >
                        {isGenerating ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Generating {progress}%
                                <div 
                                    className="absolute bottom-0 left-0 h-1 bg-primary-foreground/30 transition-all duration-300" 
                                    style={{ width: `${progress}%` }}
                                />
                            </>
                        ) : (
                            <>
                                <Download className="h-4 w-4 mr-2" />
                                Download PDF Report
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
